import { useState, useEffect, useRef } from 'react'
import { X, Check, Loader2, Calendar, CreditCard, User, FileText, Copy, QrCode, Wifi } from 'lucide-react'
import { useEfiPix } from '../hooks/useEfiPix'
import type { RecorrenciaPixResponse, SolicitacaoRecorrenciaResponse } from '../hooks/useEfiPix'
import { supabase } from '../services/supabase'
import {
    PERIODICIDADE_ADJETIVO_PLURAL,
    PERIODICIDADE_LABELS,
    normalizarPeriodicidade,
} from '../utils/periodicidadePix'
import { formatarCpf, limparCpf, validarCpf } from '../utils/cpf'

interface PixRecorrenciaModalProps {
    isOpen: boolean
    onClose: () => void
    onCancel?: () => void
    valor: number
    descricao: string
    planoId: string
    userId: string
    periodicidade?: string
    onRecorrenciaConfirmed: (payload: {
        idRec: string
        txidCobrancaImediata?: string
    }) => void
}

export function PixRecorrenciaModal({
    isOpen,
    onClose,
    onCancel,
    valor,
    descricao,
    planoId,
    userId,
    periodicidade = 'MENSAL',
    onRecorrenciaConfirmed
}: PixRecorrenciaModalProps) {
    const periodo = normalizarPeriodicidade(periodicidade)
    const labelPeriodo = PERIODICIDADE_LABELS[periodo]
    const adjetivoPlural = PERIODICIDADE_ADJETIVO_PLURAL[periodo]
    const { criarRecorrenciaPix, criarSolicitacaoRecorrencia, consultarRecorrenciaPix, loading, error } = useEfiPix()
    const [recorrencia, setRecorrencia] = useState<RecorrenciaPixResponse | null>(null)
    const [solicitacao, setSolicitacao] = useState<SolicitacaoRecorrenciaResponse | null>(null)
    const [erroLocal, setErroLocal] = useState<string | null>(null)
    const [etapa, setEtapa] = useState<'dados' | 'processando' | 'qrcode' | 'sucesso'>('dados')
    const [copied, setCopied] = useState(false)
    const [confirmandoAutomatico, setConfirmandoAutomatico] = useState(false)
    const [statusMonitoramento, setStatusMonitoramento] = useState<'aguardando' | 'confirmado' | 'erro'>('aguardando')
    const confirmacaoEnviadaRef = useRef(false)

    // Formulário de dados do usuário
    const [nome, setNome] = useState('')
    const [cpf, setCpf] = useState('')

    const emitirConfirmacao = (idRec: string, txid?: string) => {
        if (confirmacaoEnviadaRef.current) return
        confirmacaoEnviadaRef.current = true
        onRecorrenciaConfirmed({
            idRec,
            txidCobrancaImediata: txid || recorrencia?.txidCobrancaImediata || solicitacao?.txidCobrancaImediata
        })
    }

    const handleCancelar = () => {
        // Limpar estados
        setRecorrencia(null)
        setSolicitacao(null)
        setErroLocal(null)
        setEtapa('dados')
        setNome('')
        setCpf('')
        setCopied(false)
        setStatusMonitoramento('aguardando')
        confirmacaoEnviadaRef.current = false
        
        // Chamar callback de cancelamento se fornecido
        if (onCancel) {
            onCancel()
        }
        
        // Fechar modal
        onClose()
    }

    useEffect(() => {
        if (!isOpen) {
            setRecorrencia(null)
            setSolicitacao(null)
            setErroLocal(null)
            setEtapa('dados')
            setNome('')
            setCpf('')
            setCopied(false)
            setStatusMonitoramento('aguardando')
            confirmacaoEnviadaRef.current = false
            return
        }

        // Carregar dados do usuário
        if (isOpen && userId) {
            carregarDadosUsuario()
        }
    }, [isOpen, userId])

    useEffect(() => {
        if (etapa !== 'qrcode' || !recorrencia?.idRec) return

        let active = true
        const interval = setInterval(async () => {
            if (!active) return
            const detalhe = await consultarRecorrenciaPix(recorrencia.idRec)
            if (!detalhe) {
                setStatusMonitoramento('erro')
                return
            }
            const status = (detalhe?.status || '').toUpperCase()
            setStatusMonitoramento('aguardando')

            if (status === 'APROVADA' || status === 'ATIVA') {
                setConfirmandoAutomatico(true)
                setStatusMonitoramento('confirmado')
                setEtapa('sucesso')
                emitirConfirmacao(recorrencia.idRec, recorrencia.txidCobrancaImediata)
            }
        }, 6000)

        return () => {
            active = false
            clearInterval(interval)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [etapa, recorrencia?.idRec])

    const carregarDadosUsuario = async () => {
        try {
            // Primeiro, carregar apenas o nome (que sempre existe)
            const { data: nomeData, error: nomeError } = await supabase
                .from('corretores')
                .select('nome')
                .eq('user_id', userId)
                .single()

            if (nomeError) {
                console.error('Erro ao carregar nome do usuário:', nomeError)
                return
            }

            if (nomeData) {
                setNome(nomeData.nome || '')
            }

            // Tentar carregar CPF separadamente (a coluna pode não existir ainda)
            // Se a coluna não existir, o erro será ignorado e o campo ficará vazio
            try {
                const { data: cpfData, error: cpfError } = await supabase
                    .from('corretores')
                    .select('cpf')
                    .eq('user_id', userId)
                    .single()

                // Se o erro for específico sobre coluna não existir, apenas logar e continuar
                if (cpfError) {
                    if (cpfError.code === '42703' || cpfError.message?.includes('does not exist')) {
                        console.log('Coluna CPF não existe na tabela corretores. Execute o script SQL para adicioná-la.')
                    } else {
                        console.warn('Erro ao carregar CPF:', cpfError.message)
                    }
                } else if (cpfData?.cpf) {
                    setCpf(cpfData.cpf)
                }
            } catch (cpfErr: any) {
                // Se a coluna CPF não existir, o campo ficará vazio
                // O usuário poderá preencher manualmente
                if (cpfErr.code === '42703' || cpfErr.message?.includes('does not exist')) {
                    console.log('CPF não disponível na tabela. Usuário poderá preencher manualmente.')
                }
            }
        } catch (err: any) {
            console.error('Erro ao carregar dados do usuário:', err)
            // Não bloquear o uso do modal se houver erro
            // O usuário pode preencher os dados manualmente
        }
    }

    const formatarCPF = (valor: string) => formatarCpf(valor)

    const handleCPFChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const valor = e.target.value
        const formatado = formatarCPF(valor)
        setCpf(formatado)
    }

    const validarCPF = (cpfValue: string): boolean => validarCpf(cpfValue)

    const criarRecorrencia = async () => {
        if (!nome.trim()) {
            setErroLocal('Por favor, informe seu nome completo.')
            return
        }

        if (!validarCPF(cpf)) {
            setErroLocal('Por favor, informe um CPF válido.')
            return
        }

        setEtapa('processando')
        setErroLocal(null)

        try {
            // Validar CPF
            const cpfLimpo = limparCpf(cpf)
            if (!validarCpf(cpfLimpo)) {
                setErroLocal('CPF inválido. Verifique os números digitados.')
                setEtapa('dados')
                return
            }

            // Validar nome
            const nomeLimpo = nome.trim()
            if (!nomeLimpo || nomeLimpo.length < 3) {
                setErroLocal('Por favor, informe seu nome completo (mínimo 3 caracteres).')
                setEtapa('dados')
                return
            }

            // Garantir que o valor seja um número
            const valorNumerico = typeof valor === 'number' ? valor : parseFloat(String(valor))

            if (isNaN(valorNumerico) || valorNumerico <= 0) {
                setErroLocal(`Valor inválido: ${valor}. Por favor, verifique o valor do plano.`)
                setEtapa('dados')
                return
            }

            console.log('📤 Criando recorrência com dados:', {
                valor: valorNumerico,
                valorTipo: typeof valorNumerico,
                descricao: descricao || 'NÃO FORNECIDA',
                devedor_cpf: cpfLimpo ? cpfLimpo.substring(0, 3) + '***' : 'NÃO FORNECIDO',
                devedor_nome: nomeLimpo || 'NÃO FORNECIDO'
            })

            const resultado = await criarRecorrenciaPix({
                valor: valorNumerico,
                descricao: descricao || 'Assinatura',
                devedor_cpf: cpfLimpo,
                devedor_nome: nomeLimpo,
                periodicidade: periodo,
            })

            console.log('📥 Resultado da recorrência:', resultado)

            if (resultado) {
                setRecorrencia(resultado)

                // Registrar recorrência no banco de dados
                await registrarRecorrenciaNoBanco(resultado)

                const txidImediato = resultado.txidCobrancaImediata

                // Jornada 3: obter QR composto (pagamento imediato + autorização)
                console.log('📋 Obtendo QR Code da Jornada 3 (pagamento imediato + recorrência)...')
                const solicitacaoResultado = await criarSolicitacaoRecorrencia({
                    idRec: resultado.idRec,
                    valorInicial: Number(valorNumerico),
                    txid: txidImediato
                })

                if (solicitacaoResultado) {
                    setSolicitacao(solicitacaoResultado)

                    if (solicitacaoResultado.qrcode || solicitacaoResultado.imagemQrcode || solicitacaoResultado.location) {
                        setEtapa('qrcode')
                    } else if (resultado.qrcode) {
                        setSolicitacao({
                            idRec: resultado.idRec,
                            qrcode: resultado.qrcode,
                            imagemQrcode: resultado.imagemQrcode || null,
                            location: resultado.location,
                            status: resultado.status,
                            valor: valorNumerico.toFixed(2),
                            txidCobrancaImediata: txidImediato,
                            tipoJornada: resultado.tipoJornada || 'JORNADA_3',
                            jornada: 3,
                            mensagem: resultado.mensagem || 'Escaneie o QR Code para pagar agora.'
                        })
                        setEtapa('qrcode')
                    } else {
                        // Se não tem QR Code, verificar se é um erro 501 (não disponível)
                        if (solicitacaoResultado.error && solicitacaoResultado.httpStatus === 501) {
                            // Endpoint não disponível, mostrar mensagem informativa mas continuar
                            console.warn('⚠️ Endpoint não disponível. Recorrência criada, mas QR Code não disponível.')
                            // Não definir erro local, apenas continuar para sucesso
                            // O usuário verá uma mensagem informativa na tela de sucesso
                        }
                        setEtapa('sucesso')
                    }
                } else {
                    // Se não conseguiu criar solicitação, verificar o erro
                    const erroSolicitacao = error || 'Não foi possível criar solicitação de confirmação'
                    console.warn('⚠️ Não foi possível criar solicitação de confirmação:', erroSolicitacao)

                    // Se o erro indica que o endpoint não está disponível (501), mostrar mensagem amigável
                    if (erroSolicitacao.includes('não suporta') || erroSolicitacao.includes('não disponível')) {
                        setErroLocal('A recorrência foi criada com sucesso, mas a API da EFI não suporta criar solicitação de confirmação via API ainda. Entre em contato com o suporte da EFI para obter instruções sobre como gerar o QR Code.')
                    } else {
                        setErroLocal('Recorrência criada com sucesso, mas não foi possível gerar o QR Code. ' + erroSolicitacao)
                    }
                    setEtapa('sucesso')
                }
            } else {
                // Exibir erro mais detalhado
                // Priorizar erro do hook (que vem do backend) ou erro local
                const mensagemErro = error || 'Erro ao criar recorrência PIX'
                console.error('❌ Erro ao criar recorrência:', {
                    mensagem: mensagemErro,
                    error: error,
                    resultado: resultado
                })

                // Mensagem de erro mais amigável
                let mensagemExibida = mensagemErro

                // Verificar se é erro de CPF primeiro (mensagem mais específica)
                if (mensagemErro.includes('CPF inválido:')) {
                    // Usar a mensagem completa do backend que já inclui detalhes do CPF
                    mensagemExibida = mensagemErro
                } else if (mensagemErro.includes('CPF') || mensagemErro.includes('cpf')) {
                    mensagemExibida = 'CPF inválido. Verifique se o CPF está correto e contém 11 dígitos válidos.'
                } else if (mensagemErro.includes('Dados obrigatórios')) {
                    mensagemExibida = 'Por favor, preencha todos os campos obrigatórios.'
                } else if (mensagemErro.includes('Valor inválido')) {
                    mensagemExibida = 'Valor inválido. Verifique o valor do plano.'
                } else if (mensagemErro.includes('chave')) {
                    mensagemExibida = 'Chave PIX inválida. Verifique a configuração.'
                }

                setErroLocal(mensagemExibida)
                setEtapa('dados')
            }
        } catch (err: any) {
            console.error('❌ Erro ao criar recorrência:', err)
            const mensagemErro = err?.message || err?.toString() || 'Erro ao criar recorrência PIX. Verifique se o backend está rodando.'
            setErroLocal(mensagemErro)
            setEtapa('dados')
        }
    }

    const registrarRecorrenciaNoBanco = async (rec: RecorrenciaPixResponse) => {
        try {
            // Buscar corretor_id
            const { data: corretor } = await supabase
                .from('corretores')
                .select('id')
                .eq('user_id', userId)
                .single()

            if (!corretor) {
                console.error('Corretor não encontrado')
                return
            }

            // Buscar assinatura
            const { data: assinatura } = await supabase
                .from('assinaturas')
                .select('id')
                .eq('user_id', userId)
                .eq('plano_id', planoId)
                .order('created_at', { ascending: false })
                .limit(1)
                .single()

            // Normalizar status para minúsculas (a constraint do banco espera minúsculas)
            // A API da EFI retorna status em maiúsculas (ex: 'CRIADA'), mas o banco espera minúsculas (ex: 'criada')
            const statusNormalizado = rec.status?.toLowerCase() || 'criada'

            // Validar se o status está na lista permitida pela constraint
            const statusPermitidos = ['criada', 'ativa', 'aprovada', 'pausada', 'cancelada', 'expirada']
            const statusFinal = statusPermitidos.includes(statusNormalizado) ? statusNormalizado : 'criada'

            // Normalizar periodicidade também (caso venha em maiúsculas)
            const periodicidadeNormalizada = rec.periodicidade?.toUpperCase() || 'MENSAL'

            // Registrar recorrência
            const { error: recError } = await supabase
                .from('recorrencias_pix')
                .insert({
                    user_id: corretor.id,
                    assinatura_id: assinatura?.id || null,
                    id_rec_efi: rec.idRec,
                    status: statusFinal, // Usar status normalizado
                    periodicidade: periodicidadeNormalizada,
                    valor: rec.valor,
                    data_inicio: rec.dataInicio,
                    devedor_cpf: cpf.replace(/\D/g, ''),
                    devedor_nome: nome.trim(),
                    objeto_contrato: descricao,
                    location_id: rec.locationId,
                    detalhes_efi: rec
                })

            if (recError) {
                console.error('Erro ao registrar recorrência no banco:', recError)
            }
            // Pagamento fica registrado como "pago" apenas na confirmação (ativarAssinaturaEfi),
            // para evitar histórico "Pendente" no admin após o pagamento real.
        } catch (err) {
            console.error('Erro ao registrar recorrência:', err)
        }
    }

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(value)
    }

    if (!isOpen) return null

    return (
        <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={(e) => {
                // Só cancela se clicar no backdrop (não dentro do modal)
                if (e.target === e.currentTarget && etapa !== 'processando') {
                    handleCancelar()
                }
            }}
        >
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">Assinatura Recorrente - PIX</h2>
                        <p className="text-sm text-gray-600 mt-1">Configure sua assinatura {labelPeriodo.toLowerCase()} via PIX Automático</p>
                    </div>
                    <button
                        onClick={handleCancelar}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                        disabled={etapa === 'processando'}
                        title="Cancelar processo"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    {/* Etapa 1: Dados do Usuário */}
                    {etapa === 'dados' && (
                        <div className="space-y-6">
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <div className="flex items-start gap-3">
                                    <Calendar className="w-5 h-5 text-blue-600 mt-0.5" />
                                    <div className="flex-1">
                                        <h3 className="font-semibold text-blue-900 mb-1">Assinatura {labelPeriodo} Automática</h3>
                                        <p className="text-sm text-blue-800">
                                            Ao confirmar, você autoriza cobranças {adjetivoPlural} automáticas via PIX no valor de <strong>{formatCurrency(valor)}</strong>.
                                            Você poderá cancelar a qualquer momento.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        <User className="w-4 h-4 inline mr-1" />
                                        Nome Completo
                                    </label>
                                    <input
                                        type="text"
                                        value={nome}
                                        onChange={(e) => setNome(e.target.value)}
                                        placeholder="Seu nome completo"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        <FileText className="w-4 h-4 inline mr-1" />
                                        CPF
                                    </label>
                                    <input
                                        type="text"
                                        value={cpf}
                                        onChange={handleCPFChange}
                                        placeholder="000.000.000-00"
                                        maxLength={14}
                                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent ${
                                            cpf.length > 0 && !validarCPF(cpf)
                                                ? 'border-red-400 focus:ring-red-500'
                                                : 'border-gray-300 focus:ring-blue-500'
                                        }`}
                                        required
                                    />
                                    {cpf.length > 0 && !validarCPF(cpf) && (
                                        <p className="mt-1 text-xs text-red-600">CPF inválido</p>
                                    )}
                                </div>

                                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Plano:</span>
                                        <span className="font-medium text-gray-900">{descricao}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Valor {labelPeriodo}:</span>
                                        <span className="font-bold text-gray-900">{formatCurrency(valor)}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Periodicidade:</span>
                                        <span className="font-medium text-gray-900">{labelPeriodo}</span>
                                    </div>
                                </div>
                            </div>

                            {erroLocal && (
                                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                    <p className="text-sm text-red-800">{erroLocal}</p>
                                </div>
                            )}

                            <button
                                onClick={criarRecorrencia}
                                disabled={loading || !nome.trim() || !validarCPF(cpf)}
                                className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                <CreditCard className="w-5 h-5" />
                                Confirmar Assinatura Recorrente
                            </button>
                        </div>
                    )}

                    {/* Etapa 2: Processando */}
                    {etapa === 'processando' && (
                        <div className="flex flex-col items-center justify-center py-12 space-y-6">
                            <Loader2 className="w-16 h-16 text-blue-600 animate-spin" />
                            <div className="text-center space-y-2">
                                <p className="text-xl font-bold text-gray-900">Aguarde...</p>
                                <p className="text-lg text-gray-700">Finalizando o registro do PIX automático</p>
                                <p className="text-sm text-gray-500 max-w-md">
                                    Estamos criando sua assinatura recorrente no sistema da EFI.
                                    Isso pode levar alguns segundos.
                                </p>
                            </div>

                            {/* Barra de progresso animada */}
                            <div className="w-full max-w-md">
                                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                    <div className="h-full bg-blue-600 rounded-full animate-pulse" style={{ width: '70%' }}></div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Etapa 3: QR Code para Autorização */}
                    {etapa === 'qrcode' && solicitacao && (solicitacao.qrcode || solicitacao.imagemQrcode || solicitacao.location) && (
                        <div className="space-y-6">
                            <div className="flex flex-col items-center justify-center py-6">
                                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                                    <QrCode className="w-8 h-8 text-blue-600" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">Escaneie o QR Code</h3>
                                <p className="text-sm text-gray-600 text-center max-w-md">
                                    Escaneie o QR Code com o app do seu banco para <strong>pagar agora</strong> e autorizar as cobranças mensais automáticas.
                                </p>
                            </div>

                            {/* QR Code */}
                            <div className="flex flex-col items-center space-y-4">
                                <div className="bg-white p-4 rounded-lg border-2 border-gray-200 shadow-lg min-w-[256px] min-h-[256px] flex items-center justify-center">
                                    {solicitacao.imagemQrcode ? (
                                        // Priorizar imagemQrcode se disponível (é base64 da imagem)
                                        <img
                                            src={solicitacao.imagemQrcode.startsWith('data:')
                                                ? solicitacao.imagemQrcode
                                                : `data:image/png;base64,${solicitacao.imagemQrcode}`}
                                            alt="QR Code PIX"
                                            className="w-64 h-64 object-contain"
                                        />
                                    ) : solicitacao.qrcode ? (
                                        // Se não tem imagemQrcode, tentar usar qrcode
                                        solicitacao.qrcode.startsWith('http') || solicitacao.qrcode.startsWith('data:') ? (
                                            <img
                                                src={solicitacao.qrcode}
                                                alt="QR Code PIX"
                                                className="w-64 h-64 object-contain"
                                            />
                                        ) : (
                                            <img
                                                src={`data:image/png;base64,${solicitacao.qrcode}`}
                                                alt="QR Code PIX"
                                                className="w-64 h-64 object-contain"
                                                onError={() => {
                                                    console.error('Erro ao carregar QR Code')
                                                    console.log('QR Code recebido:', solicitacao.qrcode?.substring(0, 100))
                                                }}
                                            />
                                        )
                                    ) : (
                                        // Se não tem QR Code, mostrar mensagem
                                        <div className="text-center p-8 text-gray-600">
                                            <p>QR Code não disponível</p>
                                            {solicitacao.location && (
                                                <p className="text-sm text-gray-500 mt-2">Use o código PIX abaixo</p>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div className={`w-full rounded-lg p-3 border transition-all duration-500 ${statusMonitoramento === 'confirmado'
                                    ? 'bg-green-50 border-green-200'
                                    : statusMonitoramento === 'erro'
                                        ? 'bg-red-50 border-red-200'
                                        : 'bg-blue-50 border-blue-200'
                                    }`}>
                                    <div className="flex items-center gap-2">
                                        {statusMonitoramento === 'aguardando' && (
                                            <>
                                                <Loader2 className="w-4 h-4 text-blue-700 animate-spin" />
                                                <Wifi className="w-4 h-4 text-blue-700 animate-pulse" />
                                            </>
                                        )}
                                        {statusMonitoramento === 'confirmado' && (
                                            <Check className="w-4 h-4 text-green-700" />
                                        )}
                                        {statusMonitoramento === 'erro' && (
                                            <div className="w-2 h-2 rounded-full bg-red-600 animate-pulse" />
                                        )}
                                        <p className={`text-sm font-medium ${statusMonitoramento === 'confirmado'
                                            ? 'text-green-800'
                                            : statusMonitoramento === 'erro'
                                                ? 'text-red-800'
                                                : 'text-blue-800'
                                            }`}>
                                            Status da confirmação:{' '}
                                            {statusMonitoramento === 'confirmado'
                                                ? 'Confirmado no banco'
                                                : statusMonitoramento === 'erro'
                                                    ? 'Erro ao consultar, tentando novamente...'
                                                    : 'Aguardando confirmação no app do banco'}
                                        </p>
                                    </div>
                                    {statusMonitoramento === 'aguardando' && (
                                        <div className="mt-2 h-1.5 bg-blue-100 rounded-full overflow-hidden">
                                            <div className="h-full w-1/3 bg-blue-500 rounded-full animate-pulse" />
                                        </div>
                                    )}
                                </div>

                                {/* Código PIX Copia e Cola (se disponível) */}
                                {solicitacao.qrcode && !solicitacao.qrcode.startsWith('http') && !solicitacao.qrcode.startsWith('data:') && (
                                    <div className="w-full">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Código PIX (Copia e Cola):
                                        </label>
                                        <div className="flex gap-2">
                                            <textarea
                                                readOnly
                                                value={solicitacao.qrcode}
                                                className="flex-1 p-3 border border-gray-300 rounded-lg bg-gray-50 font-mono text-xs resize-none"
                                                rows={4}
                                            />
                                            <button
                                                onClick={async () => {
                                                    try {
                                                        await navigator.clipboard.writeText(solicitacao.qrcode || '')
                                                        setCopied(true)
                                                        setTimeout(() => setCopied(false), 2000)
                                                    } catch (err) {
                                                        console.error('Erro ao copiar:', err)
                                                    }
                                                }}
                                                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-2"
                                                title="Copiar código PIX"
                                            >
                                                {copied ? (
                                                    <Check className="w-5 h-5" />
                                                ) : (
                                                    <Copy className="w-5 h-5" />
                                                )}
                                            </button>
                                        </div>
                                        {copied && (
                                            <p className="text-sm text-green-600 mt-2 flex items-center gap-1">
                                                <Check className="w-4 h-4" />
                                                Código PIX copiado!
                                            </p>
                                        )}
                                    </div>
                                )}

                                {/* Informações */}
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 w-full">
                                    <div className="space-y-2 text-sm">
                                        <div className="flex justify-between">
                                            <span className="text-blue-800">Valor:</span>
                                            <span className="font-bold text-blue-900">
                                                {formatCurrency(parseFloat(solicitacao.valor))}
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-blue-800">Periodicidade:</span>
                                            <span className="font-medium text-blue-900">{labelPeriodo}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-blue-800">Tipo:</span>
                                            <span className="font-medium text-blue-900">Pagamento imediato + recorrência (Jornada 3)</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Instruções */}
                                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 w-full">
                                    <h4 className="font-semibold text-yellow-900 mb-2">📱 Como pagar:</h4>
                                    <ol className="list-decimal list-inside space-y-1 text-sm text-yellow-800">
                                        <li>Abra o app do seu banco</li>
                                        <li>Escaneie o QR Code acima ou copie o código PIX</li>
                                        <li>Confirme o pagamento</li>
                                        <li>Autorize o débito automático para próximas cobranças</li>
                                    </ol>
                                </div>

                                {confirmandoAutomatico && (
                                    <div className="w-full bg-green-50 border border-green-200 rounded-lg p-3">
                                        <p className="text-sm text-green-800">
                                            Pagamento/autorização detectado. Ativando assinatura automaticamente...
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Etapa 4: Sucesso */}
                    {etapa === 'sucesso' && recorrencia && (
                        <div className="space-y-6">
                            <div className="flex flex-col items-center justify-center py-6">
                                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-4 animate-bounce">
                                    <Check className="w-10 h-10 text-green-600" />
                                </div>
                                <h3 className="text-2xl font-bold text-gray-900 mb-2">✅ Assinatura Criada com Sucesso!</h3>
                                <p className="text-sm text-gray-600 text-center max-w-md">
                                    {solicitacao && solicitacao.qrcode
                                        ? 'Você já pode escanear o QR Code acima para autorizar o débito automático e realizar o primeiro pagamento.'
                                        : `Sua assinatura recorrente foi configurada. Você receberá cobranças ${adjetivoPlural} automaticamente via PIX.`
                                    }
                                </p>
                            </div>

                            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                <h4 className="font-semibold text-gray-900">Detalhes da Assinatura</h4>
                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">ID da Recorrência:</span>
                                        <span className="font-mono text-xs text-gray-900">{recorrencia.idRec}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Status:</span>
                                        <span className="font-medium text-green-600 capitalize">{recorrencia.status}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Valor {labelPeriodo}:</span>
                                        <span className="font-bold text-gray-900">{formatCurrency(recorrencia.valor)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Periodicidade:</span>
                                        <span className="font-medium text-gray-900">
                                            {PERIODICIDADE_LABELS[normalizarPeriodicidade(recorrencia.periodicidade || periodo)]}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Início:</span>
                                        <span className="font-medium text-gray-900">
                                            {new Date(recorrencia.dataInicio).toLocaleDateString('pt-BR')}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
                                <p className="text-sm text-green-800">
                                    <strong>🎉 Tudo pronto!</strong> {
                                        solicitacao && solicitacao.qrcode
                                            ? 'Escaneie o QR Code acima para autorizar o débito automático. Após o pagamento, as próximas cobranças serão automáticas.'
                                            : 'Você será redirecionado para o dashboard. A primeira cobrança será gerada automaticamente e você receberá o PIX para pagamento.'
                                    }
                                </p>
                            </div>

                            <button
                                onClick={() => {
                                    emitirConfirmacao(
                                        recorrencia.idRec,
                                        recorrencia.txidCobrancaImediata || solicitacao?.txidCobrancaImediata
                                    )
                                    onClose()
                                }}
                                className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                            >
                                <Check className="w-5 h-5" />
                                Ir para Dashboard
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

